import "./FilterSearchBar.scss";
import Modal from "../Modal/Modal";
import FlowTextInput from "../FlowTextInput/FlowTextInput";
import { useRef, useState, useEffect } from "react";
import Responsible from "../../pages/Tasks/Modals/Responsible/Responsible";
import TaskStatus from "../../pages/Tasks/Modals/TaskStatus/TaskStatus";
import Priority from "../../pages/Tasks/Modals/TaskPriority/TaskPriority";
import UserAvatar from "../UserAvatar/UserAvatar";
import useAuthStore from "../../store/authStore";
import { translateLabel } from "../../i18n/defaultLabels";
import { useTranslation } from "react-i18next";

// A filter button: shows its name until a value is picked, then the value
// with an ✕ to remove just that filter
function FilterChip({ icon, label, value, onOpen, onRemove }) {
  const { t } = useTranslation();
  const isActive = Boolean(value);

  return (
    <button
      type="button"
      className={`filter-chip ${isActive ? "active" : ""}`}
      onClick={onOpen}
    >
      {isActive ? (
        value
      ) : (
        <>
          <span className="material-symbols-outlined filter-chip__icon">
            {icon}
          </span>
          <span>{label}</span>
          <span className="material-symbols-outlined filter-chip__arrow">
            arrow_drop_down
          </span>
        </>
      )}
      {isActive && (
        <span
          className="material-symbols-outlined filter-chip__remove"
          role="button"
          title={t("tasks.filter.removeFilter")}
          aria-label={t("tasks.filter.removeFilter")}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          close
        </span>
      )}
    </button>
  );
}

function FilterSearchBar({
  onSearchChange,
  onResponsibleFilter,
  onStatusFilter,
  onPriorityFilter,
  onClearFilters,
  selectedResponsible,
  selectedStatus,
  selectedPriority,
  searchValue = "",
  statusConfig = [],
  priorityConfig = [],
}) {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const modalRef = useRef();
  const searchInputRef = useRef(null);
  const [searchText, setSearchText] = useState("");

  // Sync internal state with external value
  useEffect(() => {
    if (searchValue !== searchText) {
      setSearchText(searchValue);
    }
  }, [searchValue, searchText]);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  const handleSearchChange = (value) => {
    setSearchText(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  const openModal = (e, arrow, content) => {
    const triggerElement = e.currentTarget;

    if (modalRef.current) {
      modalRef.current.open({
        arrow,
        content,
        triggerElement,
      });
    }
  };

  const handleResponsibleSelect = (member) => {
    if (onResponsibleFilter) {
      onResponsibleFilter(member);
    }
    if (modalRef.current) {
      modalRef.current.close();
    }
  };

  const handleStatusSelect = (status) => {
    if (onStatusFilter) {
      onStatusFilter(status);
    }
    if (modalRef.current) {
      modalRef.current.close();
    }
  };

  const handlePrioritySelect = (priority) => {
    if (onPriorityFilter) {
      onPriorityFilter(priority);
    }
    if (modalRef.current) {
      modalRef.current.close();
    }
  };

  const handleClearFilters = () => {
    setSearchText("");
    if (onSearchChange) {
      onSearchChange("");
    }
    if (onClearFilters) {
      onClearFilters();
    }
  };

  // "Only mine" is the responsible filter set to the user
  const myId = String(user?.id || user?._id || "");
  const isOnlyMine =
    Boolean(myId) && String(selectedResponsible?.userId || "") === myId;

  const toggleOnlyMine = () => {
    if (!onResponsibleFilter) return;
    onResponsibleFilter(
      isOnlyMine
        ? null
        : {
            userId: myId,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            profile: user.profile,
          },
    );
  };

  const status = selectedStatus
    ? statusConfig.find((s) => s.label === selectedStatus)
    : null;
  const priority =
    selectedPriority !== null ? priorityConfig[selectedPriority] : null;

  const hasFilters =
    Boolean(selectedResponsible) ||
    Boolean(selectedStatus) ||
    selectedPriority !== null ||
    Boolean(searchText);

  return (
    <div className="filter-search-bar">
      <Modal ref={modalRef}></Modal>
      <div className="filter-search-wrapper">
        <div className="filter-buttons-container">
          <button
            type="button"
            className={`filter-chip ${isOnlyMine ? "active" : ""}`}
            onClick={toggleOnlyMine}
            aria-pressed={isOnlyMine}
          >
            <span className="material-symbols-outlined filter-chip__icon">
              person_check
            </span>
            <span>{t("tasks.filter.onlyMine")}</span>
          </button>

          <FilterChip
            icon="person"
            label={t("tasks.filter.responsible")}
            value={
              selectedResponsible &&
              !isOnlyMine && (
                <>
                  <UserAvatar user={selectedResponsible} size="small" />
                  <span>
                    {[
                      selectedResponsible.firstName,
                      selectedResponsible.lastName,
                    ]
                      .filter(Boolean)
                      .join(" ") || selectedResponsible.email}
                  </span>
                </>
              )
            }
            onOpen={(e) =>
              openModal(
                e,
                "top-left",
                <Responsible
                  updateResponsible={handleResponsibleSelect}
                  selectedResponsible={selectedResponsible}
                />,
              )
            }
            onRemove={() => onResponsibleFilter?.(null)}
          />

          <FilterChip
            icon="avg_pace"
            label={t("tasks.filter.status")}
            value={
              selectedStatus && (
                <>
                  <span
                    className="filter-chip__dot"
                    style={{ backgroundColor: status?.color }}
                  />
                  <span>{translateLabel(selectedStatus, t)}</span>
                </>
              )
            }
            onOpen={(e) =>
              openModal(
                e,
                "top-left",
                <TaskStatus
                  updateStatus={handleStatusSelect}
                  selectedStatus={selectedStatus}
                  showClear={true}
                  statusConfig={statusConfig}
                />,
              )
            }
            onRemove={() => onStatusFilter?.(null)}
          />

          <FilterChip
            icon="priority_high"
            label={t("tasks.filter.priority")}
            value={
              priority && (
                <>
                  <span
                    className="filter-chip__dot"
                    style={{ backgroundColor: priority.color }}
                  />
                  <span>{translateLabel(priority.label, t)}</span>
                </>
              )
            }
            onOpen={(e) =>
              openModal(
                e,
                "top-left",
                <Priority
                  updatePriority={handlePrioritySelect}
                  selectedPriority={selectedPriority}
                  priorityConfig={priorityConfig}
                />,
              )
            }
            onRemove={() => onPriorityFilter?.(null)}
          />

          {hasFilters && (
            <button
              type="button"
              className="filter-clear-all"
              onClick={handleClearFilters}
            >
              {t("tasks.filter.clearAll")}
            </button>
          )}
        </div>
        <div className="input-search-container">
          <FlowTextInput
            ref={searchInputRef}
            placeholder={t("tasks.filter.searchPlaceholder")}
            value={searchText}
            onChange={handleSearchChange}
            expandable={true}
            icon="search"
          />
        </div>
      </div>
    </div>
  );
}

export default FilterSearchBar;
